// client/src/components/pages/LexiStar/StatusBoard/StatusTable.jsx
//
// The status board's table view (LS-09 §5.3; FR-STAT-001 columns, sortable,
// user-selectable; row selection for bulk actions; rows drill through to trip
// detail — FR-STAT-009). Columns live in ./columns.

import React from 'react';
import { FaSort, FaSortUp, FaSortDown, FaHandPaper } from 'react-icons/fa';
import { COLUMNS, rowRegistration } from './columns';
import { burnIndexBand } from './fuelLogic';

const thClass =
  'px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap';

/** Conditional formatting (LS-09 §5.3): burn index, dwell breaches, staleness. */
const rowAccent = (row) => {
  const band = burnIndexBand(row.fuel?.burn_index, row.trip?.pct_distance);
  if (band === 'critical') return 'border-l-4 border-l-red-400';
  if (band === 'warning') return 'border-l-4 border-l-amber-400';
  if (row.dwell_breach) return 'border-l-4 border-l-orange-400';
  return 'border-l-4 border-l-transparent';
};

export default function StatusTable({
  rows,
  visible,
  sort,
  onSort,
  selected,
  onToggle,
  onToggleAll,
  onOpenTrip,
  onOpenRun,
  onOverride,
  canOverride,
  highlighted,
  now,
}) {
  const cols = COLUMNS.filter((c) => visible.includes(c.id));
  const ctx = { now, onOpenRun };
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.asset_key));

  const sortIcon = (id) => {
    if (sort.id !== id) return <FaSort className="inline ml-1 text-slate-300" size={10} />;
    return sort.dir === 'asc' ? (
      <FaSortUp className="inline ml-1 text-slate-600" size={10} />
    ) : (
      <FaSortDown className="inline ml-1 text-slate-600" size={10} />
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50 sticky top-0 z-[1]">
          <tr>
            <th className="px-3 py-3 w-8">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll(!allSelected)}
                aria-label="Select all rows"
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
            </th>
            {cols.map((c) => (
              <th
                key={c.id}
                className={thClass}
                aria-sort={
                  sort.id === c.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
              >
                <button
                  type="button"
                  onClick={() => onSort(c.id)}
                  className="inline-flex items-center uppercase tracking-wider hover:text-slate-900"
                >
                  {c.label}
                  {sortIcon(c.id)}
                </button>
              </th>
            ))}
            {canOverride && <th className={`${thClass} text-right`}>Actions</th>}
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-slate-200">
          {rows.map((r) => (
            <tr
              key={r.asset_key}
              aria-label={rowRegistration(r)}
              onClick={() => r.trip && onOpenTrip(r)}
              className={`${rowAccent(r)} ${r.trip ? 'cursor-pointer' : ''} ${highlighted.has(r.asset_key) ? 'bg-primary-50' : 'hover:bg-slate-50'} transition-colors`}
              data-highlight={highlighted.has(r.asset_key) ? 'true' : undefined}
            >
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(r.asset_key)}
                  onChange={() => onToggle(r.asset_key)}
                  aria-label={`Select ${rowRegistration(r)}`}
                  className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
              </td>
              {cols.map((c) => (
                <td key={c.id} className="px-3 py-3 align-middle">
                  {c.render(r, ctx)}
                </td>
              ))}
              {canOverride && (
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {r.trip && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOverride(r);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all"
                      aria-label={`Override ${rowRegistration(r)}`}
                    >
                      <FaHandPaper size={11} /> Override
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
